/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, lazy, Suspense, useState } from 'react';
import { Link } from '@/components/link';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/providers/auth';
import { useTranslation } from 'react-i18next';
import { WishlistHeartIcon } from '@/components/icons';
import { cn } from '@/lib/utils';

const SignInFlow = lazy(() =>
    import('@/components/header/user-actions/sign-in-flow').then((m) => ({ default: m.SignInFlow }))
);

const WISHLIST_ICON_CLASS =
    'cursor-pointer hover:bg-transparent hover:opacity-50 transition-opacity p-0 size-6 md:size-9 min-w-6 md:min-w-9 min-h-6 md:min-h-9 flex items-center justify-center rounded-none';

export default function WishlistIcon({ className }: { className?: string }): ReactElement {
    const session = useAuth();
    const { t } = useTranslation('header');
    const isAuthenticated = session?.userType === 'registered' && Boolean(session?.customerId);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

    if (!isAuthenticated) {
        return (
            <>
                <Button
                    type="button"
                    variant="ghost"
                    aria-label={t('wishlist')}
                    className={cn(WISHLIST_ICON_CLASS, className)}
                    onClick={() => setIsLoginModalOpen(true)}>
                    <WishlistHeartIcon className="size-6 md:size-9 p-0" />
                </Button>

                {isLoginModalOpen && (
                    <Suspense fallback={null}>
                        <SignInFlow open={isLoginModalOpen} onOpenChange={setIsLoginModalOpen} />
                    </Suspense>
                )}
            </>
        );
    }

    return (
        <Button variant="ghost" className={cn(WISHLIST_ICON_CLASS, className)} asChild>
            <Link
                to="/account/wishlist"
                aria-label={t('wishlist')}
                className="size-6 md:size-9 flex items-center justify-center p-0">
                <WishlistHeartIcon className="size-6 md:size-9 p-0" />
            </Link>
        </Button>
    );
}
